/** @file tilePaint: writes one colour into pixels of a tile, optionally mirrored across the pivot column. */
import type { Image, Rgba } from '@zilo/pose3d-image-io/types/image';
import { tileMirrorX } from './tileMirrorX';

/**
 * Sets every point (and, when `mirrorPivotX` is a number, its mirror by `tileMirrorX`) to `color`, replacing the
 * pixel (no blending: the eraser paints `[0, 0, 0, 0]`). Points outside the image are skipped. Returns whether a
 * pixel changed.
 */
export function tilePaint(image: Image, points: readonly (readonly [number, number])[], color: Rgba, mirrorPivotX: number | null): boolean {
  let changed = false;
  const put = (x: number, y: number): void => {
    if (x < 0 || y < 0 || x >= image.width || y >= image.height) return;
    const i = (y * image.width + x) * 4;
    if (color.every((c, k) => image.data[i + k] === c)) return;
    image.data.set(color, i);
    changed = true;
  };
  for (const [x, y] of points) {
    put(x, y);
    const m = mirrorPivotX === null ? null : tileMirrorX(x, image.width, mirrorPivotX);
    if (m !== null) put(m, y);
  }
  return changed;
}
